import { useCallback, useEffect, useState } from 'react';
import { getExhaustedCallbacks, retryCallback } from '../api/client';

const PER_PAGE = 25;

export default function ExhaustedList() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [retryState, setRetryState] = useState({}); // merchant_order_id -> 'pending' | 'done' | 'error'

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    try {
      const data = await getExhaustedCallbacks({ page: targetPage, perPage: PER_PAGE });
      setRows(data.data || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load exhausted callbacks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [page, load]);

  function toggleSelected(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) => {
      if (prev.size === rows.length) {
        return new Set();
      }
      return new Set(rows.map((r) => r.merchant_order_id));
    });
  }

  async function retryOne(id) {
    setRetryState((prev) => ({ ...prev, [id]: 'pending' }));
    try {
      await retryCallback(id);
      setRetryState((prev) => ({ ...prev, [id]: 'done' }));
      setRows((prev) => prev.filter((r) => r.merchant_order_id !== id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      setRetryState((prev) => ({ ...prev, [id]: 'error' }));
    }
  }

  async function retrySelected() {
    const ids = Array.from(selected);
    // Sequential, not Promise.all - this hits the same Lumen retry-callback
    // endpoint the poller/retry-exhausted services also touch (per-id
    // Cache::lock), and there's no reason to slam it with a burst of
    // concurrent admin-triggered retries just because several rows were
    // checked at once.
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await retryOne(id);
    }
  }

  const totalPages = Math.max(Math.ceil(total / PER_PAGE), 1);

  return (
    <div>
      <h1 className="h4 mb-2">Retry-exhausted callbacks</h1>
      <p className="text-muted small">
        {total} exhausted callback{total === 1 ? '' : 's'} currently waiting on manual
        intervention or the next automatic retry cycle.
      </p>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-primary btn-sm" onClick={retrySelected} disabled={selected.size === 0}>
          Retry selected ({selected.size})
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => load(page)} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-4">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          Loading…
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '2rem' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={rows.length > 0 && selected.size === rows.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Merchant order ID</th>
                  <th>Merchant ID</th>
                  <th>Attempts</th>
                  <th>Last error</th>
                  <th>Exhausted at</th>
                  <th>Created at</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.merchant_order_id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selected.has(row.merchant_order_id)}
                        onChange={() => toggleSelected(row.merchant_order_id)}
                      />
                    </td>
                    <td>{row.merchant_order_id}</td>
                    <td>{row.merchant_id ?? '—'}</td>
                    <td>
                      {row.attempts}/{row.max_attempts}
                    </td>
                    <td className="text-truncate" style={{ maxWidth: '260px' }} title={row.last_error || ''}>
                      {row.last_error || '—'}
                    </td>
                    <td>{row.exhausted_at || '—'}</td>
                    <td>{row.created_at || '—'}</td>
                    <td className="text-nowrap">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => retryOne(row.merchant_order_id)}
                        disabled={retryState[row.merchant_order_id] === 'pending'}
                      >
                        {retryState[row.merchant_order_id] === 'pending' ? 'Retrying…' : 'Retry'}
                      </button>
                      {retryState[row.merchant_order_id] === 'error' && (
                        <span className="badge text-bg-danger ms-2">failed</span>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      Nothing exhausted right now.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <nav className="d-flex align-items-center gap-3 mt-3">
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page <= 1}
        >
          Previous
        </button>
        <span className="text-muted small">
          Page {page} of {totalPages}
        </span>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          disabled={page >= totalPages}
        >
          Next
        </button>
      </nav>
    </div>
  );
}
